import { idSchema } from '@/lib/utils'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { z } from 'zod/v4'

export const customerIdSchema = z.object({ customerId: idSchema('customer') })

const customerAddressSchema = z
  .object({
    name: z.string().optional().nullable(),
    line1: z.string().optional().nullable(),
    line2: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    postal_code_suffix: z.string().nullish(),
    postal_code: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
    timezone: z.string().optional().nullable(),
    lat: z.union([z.number(), z.string(), z.null()]).optional().nullable(),
    lng: z.union([z.number(), z.string(), z.null()]).optional().nullable(),
    google_place_id: z.string().optional().nullable(),
    is_verified: z.boolean().optional().nullable(),
    is_rooftop: z.boolean().optional().nullable(),
  })
  .optional()
  .nullable()

export const customerInternalNoteSchema = z.object({
  note: z.string().min(1, 'Note is required').max(10000, 'Note is too long'),
  timestamp: z.string().min(1, 'Timestamp is required'),
})

const customerIdentityFields = {
  name: z.string().optional().nullable(),
  email: z
    .union([z.email(), z.literal('')], 'Please enter a valid email')
    .optional()
    .nullable(),
  phone: z.string().optional().nullable(),
  customer_type: z.enum(['individual', 'business']).optional().nullable(),
}

const customerPaymentTermValues = Object.values(CustomerPaymentTerms).map((term) => term.Value)

export const customerContactSchema = z.object({
  id: z.number().optional().nullable(),
  name: z.string().trim().min(1, 'Contact name is required').max(255),
  email: z
    .union([z.email(), z.literal('')], 'Please enter a valid email')
    .optional()
    .nullable(),
  phone: z.string().optional().nullable(),
  is_primary: z.boolean().optional().nullable(),
})

const hasCustomerContact = (data: { email?: string | null; phone?: string | null }) => {
  const hasEmail = data.email && data.email.trim() !== ''
  const hasPhone = data.phone && data.phone.trim() !== ''
  return hasEmail || hasPhone
}

const customerContactRequiredMessage = {
  message: 'Either phone or email is required',
  // No path specified - this makes it a form-level error that clears on any field interaction
  // so the error won't show for just one field and updating either will allow the user to resubmit
}

export const customerIdentitySchema = z
  .object(customerIdentityFields)
  .refine(hasCustomerContact, customerContactRequiredMessage)

export const customerSchema = z
  .object({
    ...customerIdentityFields,
    is_business: z.boolean().optional().nullable(),
    pay_later_allowed: z.boolean().optional().nullable(),
    allow_fulfillment_before_payment: z.boolean().optional().nullable(),
    statement_delivery_method: z.enum(['automatic', 'email', 'print']).optional(),
    billing_email: z
      .union([z.email(), z.literal('')], 'Please enter a valid billing email')
      .optional()
      .nullable(),
    credit_limit: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
    payment_terms_code: z.enum(customerPaymentTermValues).optional().nullable(),
    payment_terms_days: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
    tax_exempt: z.boolean().optional().nullable(),
    physical_address: customerAddressSchema,
    billing_address: customerAddressSchema,
    physical_address_is_billing: z.boolean().optional().nullable(),
    internal_notes: z.array(customerInternalNoteSchema).optional().nullable(),
    contacts: z.array(customerContactSchema).optional().nullable(),
  })
  .refine(hasCustomerContact, customerContactRequiredMessage)

export const customerOccasionReminderSchema = z
  .object({
    occasion_type: z.enum(['birthday', 'anniversary', 'valentines_day', 'mothers_day', 'other']),
    occasion_label: z.string().optional().nullable(),
    honoree_name: z.string().optional().nullable(),
    occasion_on: z.iso.date().optional().nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.occasion_type === 'other' && !value.occasion_label?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Occasion label is required',
        path: ['occasion_label'],
      })
    }

    if (!['valentines_day', 'mothers_day'].includes(value.occasion_type) && !value.occasion_on) {
      ctx.addIssue({
        code: 'custom',
        message: 'Occasion date is required',
        path: ['occasion_on'],
      })
    }
  })
